
import { GRAPH_COLORS, NODE_DIMENSIONS } from '../constants/erd.constant';
import { KnowledgeGraphEdgeConnection, KnowledgeGraphSchemaAttributes, KnowledgeGraphSummary } from '../types/api-response';
import { EdgeConnectionGroup, EdgeGroup, EdgeType, ERDEdge, ERDNode } from '../types/erd';

export function getCSSVar(varName: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
}

export function calculateCurveOffset(dist: number, hasReverse: boolean): number {
  return hasReverse ? dist * 0.2 : 0;
}

export function calculateMidpoint(x1: number, y1: number, x2: number, y2: number): { x: number; y: number } {
  return {
    x: (x1 + x2) / 2,
    y: (y1 + y2) / 2,
  };
}

export function calculateDistance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

export function categorizeEdgeType(isSelfLoop: boolean, hasMultipleEdges: boolean): ERDEdge['type'] {
  return isSelfLoop ? 'self-loop' : hasMultipleEdges ? 'split-merge' : 'one-to-one';
}

export function getNodeDimensions(label: string): { width: number; height: number; radius: number } {
  const calculatedWidth = Math.max(
    NODE_DIMENSIONS.BASE_WIDTH,
    label.length * NODE_DIMENSIONS.CHAR_WIDTH + NODE_DIMENSIONS.PADDING
  );
  const width = Math.min(calculatedWidth, NODE_DIMENSIONS.MAX_WIDTH);
  return {
    width,
    height: NODE_DIMENSIONS.HEIGHT,
    radius: width / 2,
  };
}

export function groupEdgesBySourceTarget(edges: ERDEdge[]): Map<string, EdgeGroup> {
  const groupedEdges = new Map<string, EdgeGroup>();
  edges.forEach((edge) => {
    const key = `${edge.source}->${edge.target}`;
    if (!groupedEdges.has(key)) {
      groupedEdges.set(key, { source: edge.source, target: edge.target, edges: [] });
    }
    groupedEdges.get(key)!.edges.push(edge);
  });
  return groupedEdges;
}

export function transformSchemaToERD(schemaAttr: KnowledgeGraphSchemaAttributes, summary: KnowledgeGraphSummary): { nodes: ERDNode[]; edges: ERDEdge[] } {
  const { labels, relTypes } = summary;
  const relTypeIndex: Record<string, number> = {};

  for (const [label, relations] of Object.entries(relTypes)) {
    for (const relation of relations) {
      const key: string = `${label}|${relation.source}|${relation.target}`;
      relTypeIndex[key] = relation.count;
    }
  }

  const nodes: ERDNode[] = Object.keys(schemaAttr.schema.nodes).map(
    (nodeKey: string, index: number): ERDNode => ({
      id: nodeKey,
      label: nodeKey,
      type: 'kg-node',
      properties: schemaAttr.schema.nodes[nodeKey].properties ?? {},
      background: GRAPH_COLORS[index % GRAPH_COLORS.length].background,
      color: GRAPH_COLORS[index % GRAPH_COLORS.length].color,
      description: schemaAttr.schema.nodes[nodeKey].description || '',
      count: labels[nodeKey] ?? 0
    })
  );

  let edgeId: number = 0;
  const connectionGroups: Map<string, EdgeConnectionGroup[]> = new Map();

  for (const [edgeLabel, edgeInfo] of Object.entries(schemaAttr.schema.edges)) {
    let connectionsArray: KnowledgeGraphEdgeConnection[] = [];
    if (Array.isArray(edgeInfo.connections) && edgeInfo.connections.length) {
      connectionsArray = edgeInfo.connections;
    }
    for (const connection of connectionsArray) {
      const id: string = `${connection.in}-${connection.out}-${edgeId++}`;
      const key: string = `${connection.in}-${connection.out}`;
      if (!connectionGroups.has(key)) {
        connectionGroups.set(key, []);
      }
      const group: EdgeConnectionGroup[] | undefined = connectionGroups.get(key);
      if (group) {
        group.push({ ...connection, label: edgeLabel, id, properties: edgeInfo.properties, description: edgeInfo.description || '' });
      }
    }
  }

  const edges: ERDEdge[] = [];
  connectionGroups.forEach(
  (connections: EdgeConnectionGroup[], key: string): void => {
    const keyParts: string[] = key.split('-');
    const source: string = keyParts[0];
    const target: string = keyParts[1];

    const edgeType: EdgeType = categorizeEdgeType(
      source === target,
      connections.length > 1
    );

    edges.push(...mapConnectionsToEdges(connections, edgeType, relTypeIndex));
  }
);
  return { nodes, edges };
}

export function mapConnectionsToEdges(connections: EdgeConnectionGroup[], edgeType: EdgeType, relTypeIndex: Record<string, number>): ERDEdge[] {
  return connections.map((connection: EdgeConnectionGroup): ERDEdge => {
    const { id, in: sourceNode, out: targetNode, label, properties, description } = connection;
    const relKey: string = `${label}|${sourceNode}|${targetNode}`;
    const edge: ERDEdge = { id, source: sourceNode, target: targetNode, type: edgeType, label, properties, description, count: relTypeIndex[relKey] ?? 0 };
    return edge;
  });
}
